// Settings → Working Hours tab.
//
// Source-of-truth render lives in portal-standalone.html:
//   • Tab button:          ~2755   (settings-tab[data-tab="working-hours"])
//   • Renderer:            8742-8831 (renderWorkingHoursPanel)
//   • Save handler:        8849-8879 (kosWHSave; note: source has a known
//     reference to non-existent #wh-ooo-reply input — we drop that field)
//
// Spec — TKOS_MASTER_CONTEXT.md §SETTINGS / Working Hours; inventory
// 01_retailer.md §7. Master spec: Mon-Sun grid with toggle + open/close
// times, "Apply to all weekdays" / "Apply to weekend" shortcuts, used to
// gate booking slots and AI auto-replies. The portal-standalone version
// adds OOO override fields — we keep that section because it's already
// wired to the backend and downstream callers (AI auto-reply) read it.
//
// Backend wiring
// --------------
// kitchenos-api/modules/customer_portal.js:1414-1435 exposes:
//   GET  /api/retailer/working-hours
//   POST /api/retailer/working-hours       body: { days, ooo, timezone? }
// The dev portal uses the spec-mandated path
// `/api/portal/settings/working-hours` first; on 404/501 we fall back to
// the legacy `/api/retailer/working-hours` so existing data round-trips.

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab, TabCleanup } from "../types";
import type { PageContext } from "../../../shell/types";

import "./working-hours.css";

// ── Types ────────────────────────────────────────────────────────────────────

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

interface DayConfig {
  open: boolean;
  start: string; // HH:MM
  end:   string; // HH:MM
}

interface OOOConfig {
  enabled:    boolean;
  message:    string;
  returnDate: string; // yyyy-mm-dd or empty
}

interface WorkingHours {
  days: Record<DayKey, DayConfig>;
  ooo:  OOOConfig;
}

interface WorkingHoursResponse {
  ok?:           boolean;
  workingHours?: Partial<WorkingHours>;
}

const DAYS: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const WEEKDAY_KEYS: DayKey[] = ["mon", "tue", "wed", "thu", "fri"];
const WEEKEND_KEYS: DayKey[] = ["sat", "sun"];

const LS_KEY = "tkos.settings.working_hours";

// ── Defaults & validation ────────────────────────────────────────────────────

function defaultDay(open = true, start = "09:00", end = "17:30"): DayConfig {
  return { open, start, end };
}

function defaultConfig(): WorkingHours {
  return {
    days: {
      mon: defaultDay(true,  "09:00", "17:30"),
      tue: defaultDay(true,  "09:00", "17:30"),
      wed: defaultDay(true,  "09:00", "17:30"),
      thu: defaultDay(true,  "09:00", "17:30"),
      fri: defaultDay(true,  "09:00", "17:30"),
      sat: defaultDay(true,  "10:00", "15:00"),
      sun: defaultDay(false, "10:00", "16:00"),
    },
    ooo: { enabled: false, message: "", returnDate: "" },
  };
}

function isHHMM(s: unknown): s is string {
  return typeof s === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
}

function sanitiseDay(input: Partial<DayConfig> | undefined, fallback: DayConfig): DayConfig {
  return {
    open:  typeof input?.open === "boolean" ? input.open : fallback.open,
    start: isHHMM(input?.start) ? input!.start! : fallback.start,
    end:   isHHMM(input?.end)   ? input!.end!   : fallback.end,
  };
}

function sanitiseConfig(input: Partial<WorkingHours> | null | undefined): WorkingHours {
  const def = defaultConfig();
  const days = {} as Record<DayKey, DayConfig>;
  for (const d of DAYS) {
    days[d.key] = sanitiseDay(input?.days?.[d.key], def.days[d.key]);
  }
  const ooo = {
    enabled:    Boolean(input?.ooo?.enabled),
    message:    typeof input?.ooo?.message === "string" ? String(input!.ooo!.message).slice(0, 500) : "",
    returnDate: typeof input?.ooo?.returnDate === "string" ? String(input!.ooo!.returnDate).slice(0, 32) : "",
  };
  return { days, ooo };
}

// ── Persistence ──────────────────────────────────────────────────────────────

function loadLocal(): WorkingHours | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return sanitiseConfig(JSON.parse(raw) as Partial<WorkingHours>);
  } catch {
    return null;
  }
}

function saveLocal(cfg: WorkingHours): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch { /* non-fatal */ }
}

async function fetchRemote(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<WorkingHours | null> {
  // Try the spec-mandated path, then the legacy customer_portal one.
  const paths = [
    "/portal/settings/working-hours",
    "/retailer/working-hours",
  ];
  for (const p of paths) {
    try {
      const res = await ctx.api<WorkingHoursResponse>(p, { signal });
      if (res?.workingHours) {
        return sanitiseConfig(res.workingHours);
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
        continue; // try next path
      }
      throw err;
    }
  }
  return null;
}

async function saveRemote(
  ctx: PageContext,
  cfg: WorkingHours,
  signal: AbortSignal,
): Promise<{ remote: boolean }> {
  const body = JSON.stringify({ days: cfg.days, ooo: cfg.ooo });
  const paths = [
    "/portal/settings/working-hours",
    "/retailer/working-hours",
  ];
  let lastErr: unknown = null;
  for (const p of paths) {
    try {
      await ctx.api<WorkingHoursResponse>(p, { method: "POST", body, signal });
      return { remote: true };
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
        lastErr = err;
        continue;
      }
      throw err;
    }
  }
  // All routes 404/501 — graceful local-only save.
  if (lastErr) {
    return { remote: false };
  }
  return { remote: false };
}

// ── Render helpers ───────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function rowHtml(day: { key: DayKey; label: string }, dc: DayConfig): string {
  return `
    <div class="set-wh-row" data-day="${day.key}">
      <div class="set-wh-day">${escapeHtml(day.label)}</div>
      <label class="set-wh-toggle">
        <input type="checkbox" data-f="open"
               ${dc.open ? "checked" : ""}
               aria-label="${escapeHtml(day.label)} open" />
        <span class="set-wh-track"><span class="set-wh-thumb"></span></span>
      </label>
      <div class="set-wh-times" data-role="times" ${dc.open ? "" : 'data-closed="1"'}>
        <input type="time" class="set-wh-time" data-f="start"
               value="${escapeHtml(dc.start)}"
               aria-label="${escapeHtml(day.label)} open time" />
        <span class="set-wh-sep">to</span>
        <input type="time" class="set-wh-time" data-f="end"
               value="${escapeHtml(dc.end)}"
               aria-label="${escapeHtml(day.label)} close time" />
      </div>
      <span class="set-wh-closed-pill" aria-hidden="true">Closed</span>
    </div>
  `;
}

const tab: SettingsTab = {
  id: "working-hours",
  label: "Working Hours",
  // No clock icon in the catalogue; `sun` reads as "daytime / working
  // hours" without introducing a new icon path.
  icon: iconMarkup("sun", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    let state: WorkingHours = loadLocal() ?? defaultConfig();
    const controller = new AbortController();
    let disposed = false;

    container.innerHTML = `
      <section class="set-wh-root" aria-labelledby="set-wh-title">
        <header class="set-wh-header">
          <h2 id="set-wh-title" class="set-section-title">Business hours</h2>
          <p class="set-help">Used to enforce booking slots and to set when AI auto-replies are sent.</p>
        </header>

        <div class="set-wh-shortcut-bar">
          <button type="button" class="set-btn set-btn-secondary" data-role="apply-weekdays">
            Apply Monday's hours to all weekdays
          </button>
          <button type="button" class="set-btn set-btn-secondary" data-role="apply-weekend">
            Apply Saturday's hours to weekend
          </button>
        </div>

        <div class="set-wh-grid" data-role="grid">
          ${DAYS.map((d) => rowHtml(d, state.days[d.key])).join("")}
        </div>

        <div class="set-wh-ooo" data-role="ooo">
          <label class="set-wh-ooo-head">
            <input type="checkbox" data-f="ooo-enabled" ${state.ooo.enabled ? "checked" : ""} />
            <span>Out of office override</span>
          </label>
          <p class="set-help">When enabled, all hours are overridden and an auto-reply is sent.</p>
          <div class="set-wh-ooo-fields" data-role="ooo-fields" ${state.ooo.enabled ? "" : 'data-collapsed="1"'}>
            <div class="set-field">
              <label for="set-wh-ooo-msg">Auto-reply message</label>
              <textarea id="set-wh-ooo-msg" class="set-textarea" data-f="ooo-message"
                        placeholder="e.g. We're closed for the holidays and will reply when we reopen.">${escapeHtml(state.ooo.message)}</textarea>
            </div>
            <div class="set-field">
              <label for="set-wh-ooo-date">Return date (optional)</label>
              <input id="set-wh-ooo-date" class="set-input" type="date"
                     data-f="ooo-date" value="${escapeHtml(state.ooo.returnDate)}" />
            </div>
          </div>
        </div>

        <div class="set-wh-actions">
          <button type="button" class="set-btn" data-role="save">Save</button>
        </div>
      </section>
    `;

    const gridEl = container.querySelector<HTMLElement>("[data-role='grid']")!;
    const oooEl = container.querySelector<HTMLElement>("[data-role='ooo']")!;
    const oooFieldsEl = container.querySelector<HTMLElement>("[data-role='ooo-fields']")!;
    const applyWeekdaysBtn = container.querySelector<HTMLButtonElement>("[data-role='apply-weekdays']")!;
    const applyWeekendBtn = container.querySelector<HTMLButtonElement>("[data-role='apply-weekend']")!;
    const saveBtn = container.querySelector<HTMLButtonElement>("[data-role='save']")!;

    const updateRowVisuals = (key: DayKey): void => {
      const row = gridEl.querySelector<HTMLElement>(`.set-wh-row[data-day='${key}']`);
      if (!row) return;
      const dc = state.days[key];
      const times = row.querySelector<HTMLElement>("[data-role='times']");
      if (times) {
        if (dc.open) times.removeAttribute("data-closed");
        else times.setAttribute("data-closed", "1");
      }
      row.classList.toggle("set-wh-row--closed", !dc.open);
      const startInp = row.querySelector<HTMLInputElement>("input[data-f='start']");
      const endInp = row.querySelector<HTMLInputElement>("input[data-f='end']");
      if (startInp) startInp.value = dc.start;
      if (endInp) endInp.value = dc.end;
      const toggle = row.querySelector<HTMLInputElement>("input[data-f='open']");
      if (toggle) toggle.checked = dc.open;
    };

    const renderAllRows = (): void => {
      DAYS.forEach((d) => updateRowVisuals(d.key));
    };

    const renderOOO = (): void => {
      const cb = oooEl.querySelector<HTMLInputElement>("input[data-f='ooo-enabled']");
      if (cb) cb.checked = state.ooo.enabled;
      if (state.ooo.enabled) oooFieldsEl.removeAttribute("data-collapsed");
      else oooFieldsEl.setAttribute("data-collapsed", "1");
      const msgEl = oooEl.querySelector<HTMLTextAreaElement>("textarea[data-f='ooo-message']");
      if (msgEl && msgEl.value !== state.ooo.message) msgEl.value = state.ooo.message;
      const dateEl = oooEl.querySelector<HTMLInputElement>("input[data-f='ooo-date']");
      if (dateEl && dateEl.value !== state.ooo.returnDate) dateEl.value = state.ooo.returnDate;
    };

    const onGridChange = (e: Event): void => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      const row = t.closest<HTMLElement>(".set-wh-row");
      if (!row) return;
      const key = row.getAttribute("data-day") as DayKey | null;
      if (!key || !state.days[key]) return;
      const field = t.getAttribute("data-f");
      const dc = state.days[key];

      if (field === "open") {
        dc.open = t.checked;
        updateRowVisuals(key);
      } else if (field === "start" && isHHMM(t.value)) {
        dc.start = t.value;
      } else if (field === "end" && isHHMM(t.value)) {
        dc.end = t.value;
      }
      saveLocal(state);
    };

    const onOOOChange = (e: Event): void => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      const f = t.getAttribute("data-f");
      if (f === "ooo-enabled" && t instanceof HTMLInputElement) {
        state.ooo.enabled = t.checked;
        renderOOO();
      } else if (f === "ooo-message" && t instanceof HTMLTextAreaElement) {
        state.ooo.message = t.value.slice(0, 500);
      } else if (f === "ooo-date" && t instanceof HTMLInputElement) {
        state.ooo.returnDate = t.value.slice(0, 32);
      }
      saveLocal(state);
    };

    const applyToWeekdays = (): void => {
      const ref = state.days.mon;
      WEEKDAY_KEYS.forEach((k) => {
        state.days[k] = { open: ref.open, start: ref.start, end: ref.end };
      });
      renderAllRows();
      saveLocal(state);
      ctx.showToast("Monday's hours applied to all weekdays", "info");
    };

    const applyToWeekend = (): void => {
      const ref = state.days.sat;
      WEEKEND_KEYS.forEach((k) => {
        state.days[k] = { open: ref.open, start: ref.start, end: ref.end };
      });
      renderAllRows();
      saveLocal(state);
      ctx.showToast("Saturday's hours applied to weekend", "info");
    };

    const onSave = async (): Promise<void> => {
      // Validate that for any open day, end > start.
      for (const d of DAYS) {
        const dc = state.days[d.key];
        if (dc.open && dc.start >= dc.end) {
          ctx.showToast(`${d.label}: close time must be after open time`, "error");
          return;
        }
      }
      saveBtn.disabled = true;
      const orig = saveBtn.textContent;
      saveBtn.textContent = "Saving…";
      try {
        const { remote } = await saveRemote(ctx, state, controller.signal);
        saveLocal(state);
        ctx.showToast(
          remote ? "Working hours saved" : "Saved locally (server route not yet wired)",
          "success",
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't save (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't save working hours";
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) {
          saveBtn.disabled = false;
          saveBtn.textContent = orig;
        }
      }
    };

    gridEl.addEventListener("change", onGridChange);
    gridEl.addEventListener("input", onGridChange);
    oooEl.addEventListener("change", onOOOChange);
    oooEl.addEventListener("input", onOOOChange);
    applyWeekdaysBtn.addEventListener("click", applyToWeekdays);
    applyWeekendBtn.addEventListener("click", applyToWeekend);
    saveBtn.addEventListener("click", () => {
      void onSave();
    });

    // First paint reflects state (already in HTML); kick off remote merge.
    renderAllRows();
    renderOOO();
    void (async () => {
      try {
        const remote = await fetchRemote(ctx, controller.signal);
        if (disposed || controller.signal.aborted) return;
        if (remote) {
          state = remote;
          saveLocal(state);
          renderAllRows();
          renderOOO();
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        // eslint-disable-next-line no-console
        console.warn("[settings/working-hours] remote fetch failed:", err);
      }
    })();

    return (): void => {
      disposed = true;
      controller.abort();
      gridEl.removeEventListener("change", onGridChange);
      gridEl.removeEventListener("input", onGridChange);
      oooEl.removeEventListener("change", onOOOChange);
      oooEl.removeEventListener("input", onOOOChange);
    };
  },
};

export default tab;
